import React, {useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import AppConstants from '../Constants/AppConstants';
import networkService from '../Network/NetworkManager';
import {ThemeContext} from '../HelperClasses/ThemeContext';
import {useLocale} from '../Localization/LocaleContext';
import {getMediaName, getMediaSize, getMediaTypeLabel, isMediaFolder, isMediaImage} from '../MediaManager/mediaUtils';

const APPURL = require('../Constants/URLConstants');
const mediaTypes = ['all', 'image', 'video', 'audio', 'pdf', 'csv'];
const mediaTypeIcons = {
  all: '✦',
  image: '▧',
  video: '▶',
  audio: '♫',
  pdf: 'PDF',
  csv: 'CSV',
};

const MediaPreview = ({item}) => {
  const {translate: t} = useLocale();
  const [failed, setFailed] = useState(false);
  if (!isMediaImage(item) || failed) {
    return <span>{failed && isMediaImage(item) ? t('media_picker.type.image') : t(`media_picker.type.${getMediaTypeLabel(item).toLowerCase()}`, getMediaTypeLabel(item))}</span>;
  }
  return <img src={item.url} alt="" onError={() => setFailed(true)} />;
};

const MediaPickerModal = ({
  isOpen = false,
  onClose,
  onSelect,
  onUpload,
  multiple = false,
  allowedTypes,
  title = 'Select media',
}) => {
  const {translate: t} = useLocale();
  const theme = React.useContext(ThemeContext);
  const folderDraftKey = `arcticwhale:${AppConstants.tenantName || 'current-tenant'}:${AppConstants.userId || 'current-user'}:media-picker:new-folder`;
  const [media, setMedia] = useState([]);
  const [folderId, setFolderId] = useState(0);
  const [breadcrumbs, setBreadcrumbs] = useState([{id: 0, name: t('media_picker.type.media')}]);
  const [query, setQuery] = useState('');
  const [type, setType] = useState('all');
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newFolderName, setNewFolderName] = useState(() => {
    if (typeof window === 'undefined') return '';
    try { return window.localStorage.getItem(folderDraftKey) || ''; } catch (error) { return ''; }
  });
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (newFolderName) window.localStorage.setItem(folderDraftKey, newFolderName);
      else window.localStorage.removeItem(folderDraftKey);
    } catch (error) { /* Draft persistence is best effort. */ }
  }, [folderDraftKey, newFolderName]);

  const loadMedia = async parent => {
    setLoading(true);
    setError('');
    try {
      const response = await networkService.networkCall(
        `${APPURL.URLPaths.media}?page=1&parent=${encodeURIComponent(parent)}`,
        'get',
      );
      if (response?.status !== true) throw new Error(t('media_picker.error.load'));
      setMedia(response?.data?.media || []);
    } catch (loadError) {
      setMedia([]);
      setError(t('media_picker.error.load_retry'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setFolderId(0);
    setBreadcrumbs([{id: 0, name: t('media_picker.type.media')}]);
    setQuery('');
    setType('all');
    setSelected([]);
    setNewFolderName('');
    loadMedia(0);
  }, [isOpen]);

  const visibleMedia = useMemo(() => {
    const allowed = allowedTypes?.map(value => value.toLowerCase());
    const normalizedQuery = query.trim().toLowerCase();
    return media.filter(item => {
      const folder = isMediaFolder(item);
      const itemType = getMediaTypeLabel(item).toLowerCase();
      const matchesType = folder || type === 'all' || itemType === type;
      const matchesAllowed = folder || !allowed?.length || allowed.includes(itemType);
      const matchesQuery = !normalizedQuery || getMediaName(item).toLowerCase().includes(normalizedQuery);
      return matchesType && matchesAllowed && matchesQuery;
    });
  }, [allowedTypes, media, query, type]);

  const openFolder = folder => {
    const next = {id: folder.id, name: getMediaName(folder)};
    setFolderId(folder.id);
    setBreadcrumbs(current => [...current, next]);
    loadMedia(folder.id);
  };

  const goToBreadcrumb = crumbIndex => {
    const crumb = breadcrumbs[crumbIndex];
    if (!crumb) return;
    setFolderId(crumb.id);
    setBreadcrumbs(current => current.slice(0, crumbIndex + 1));
    loadMedia(crumb.id);
  };

  const toggleSelection = item => {
    if (multiple) {
      setSelected(current => current.some(entry => entry.id === item.id)
        ? current.filter(entry => entry.id !== item.id)
        : [...current, item]);
      return;
    }
    setSelected([item]);
  };

  const confirmSelection = () => {
    if (!selected.length) return;
    onSelect?.(multiple ? selected : selected[0]);
    onClose?.();
  };

  const closePicker = () => {
    try { window.localStorage.removeItem(folderDraftKey); } catch (error) { /* Ignore storage failures. */ }
    setNewFolderName('');
    onClose?.();
  };

  const createFolder = async event => {
    event.preventDefault();
    const name = newFolderName.trim();
    if (!name) return;
    setCreatingFolder(true);
    setError('');
    try {
      const response = await networkService.networkCall(
        APPURL.URLPaths.media,
        'post',
        JSON.stringify({media: [{name, type: 2, parent: Number(folderId) || 0}]}),
      );
      const folder = response?.data?.media?.[0] || response?.data?.folder;
      if (response?.status !== true && response?.status !== 'true') {
        throw new Error(
          response?.data?.error_message || response?.error_message || t('media_picker.error.create_folder'),
        );
      }
      setNewFolderName('');
      try { window.localStorage.removeItem(folderDraftKey); } catch (error) { /* Ignore storage failures. */ }
      await loadMedia(folderId);
    } catch (createError) {
      setError(createError?.message || t('media_picker.error.create_folder_retry'));
    } finally {
      setCreatingFolder(false);
    }
  };

  const uploadFiles = async event => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    setUploading(true);
    setError('');
    try {
      const signedResponse = await networkService.networkCall(
        APPURL.URLPaths.S3signedUploadURL,
        'POST',
        JSON.stringify({files: files.map(file => ({
          name: file.name,
          type: file.type || 'application/octet-stream',
        }))}),
      );
      if (signedResponse?.status !== true) throw new Error(t('media_picker.error.prepare_upload'));
      const results = signedResponse?.data?.result || [];
      const uploadedUrls = await Promise.all(files.map(async (file, index) => {
        const result = results[index];
      if (!result?.signedUrl || !result?.fileUri) throw new Error(t('media_picker.error.upload_url'));
        await networkService.uploadFileWithSignedURL(
          result.signedUrl,
          file.type || 'application/octet-stream',
          file,
        );
        return result.fileUri;
      }));
      const uploadedMedia = uploadedUrls.map((url, index) => ({
        url,
        type: 1,
        parent: folderId,
        mime_type: files[index]?.type || 'application/octet-stream',
        name: files[index]?.name || getMediaName({url}),
      }));
      const response = await networkService.networkCall(
        APPURL.URLPaths.media,
        'post',
        JSON.stringify({media: uploadedMedia}),
      );
      if (response?.status !== true) throw new Error(t('media_picker.error.register_media'));
      onUpload?.(uploadedMedia);
      await loadMedia(folderId);
    } catch (uploadError) {
      setError(t('media_picker.error.upload_retry'));
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen || typeof document === 'undefined') return null;
  const colors = theme?.colors || {};
  const selectedIds = new Set(selected.map(item => item.id));
  const options = allowedTypes?.length ? ['all', ...allowedTypes.map(value => value.toLowerCase()).filter(value => value !== 'all')] : mediaTypes;

  return createPortal(
    <div className="media-picker-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="media-picker-modal" style={{backgroundColor: colors.cardBackground || 'var(--color-surface)', color: colors.primaryText || 'var(--color-text)'}}>
        <div className="media-picker-header">
          <div>
            <h2>{title}</h2>
            <div className="media-picker-breadcrumbs">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={`${crumb.id}-${index}`}>
                  {index > 0 && <span>/</span>}
                  <button type="button" onClick={() => goToBreadcrumb(index)}>{crumb.name}</button>
                </React.Fragment>
              ))}
            </div>
          </div>
          <button type="button" className="media-picker-close" onClick={closePicker} aria-label={t('media_picker.action.close')}>×</button>
        </div>

        <div className="media-picker-toolbar">
          <span className="media-picker-show-label">{t('media_picker.label.show')}</span>
          <div className="media-picker-type-filters" role="group" aria-label={t('media_picker.label.media_type')}>
            {options.map(option => (
              <button key={option} type="button" title={t('media_picker.action.show_type', {type: t(`media_picker.type.${option}`, option)})} aria-label={t('media_picker.action.show_type', {type: t(`media_picker.type.${option}`, option)})} className={type === option ? 'active' : ''} onClick={() => setType(option)}>
                {mediaTypeIcons[option] || option.toUpperCase()}
              </button>
            ))}
          </div>
          <input value={query} onChange={event => setQuery(event.target.value)} placeholder={t('media_picker.placeholder.search')} aria-label={t('media_picker.placeholder.search')} />
          <input ref={fileInputRef} type="file" multiple hidden onChange={uploadFiles} />
          <button type="button" className="media-picker-upload-icon" title={t('media_picker.action.upload')} aria-label={t('media_picker.action.upload')} disabled={uploading} onClick={() => fileInputRef.current?.click()}>
            {uploading ? '…' : '↑'}
          </button>
          <form className="media-picker-folder-form" onSubmit={createFolder}>
            <input value={newFolderName} onChange={event => setNewFolderName(event.target.value)} placeholder={t('media_picker.placeholder.folder')} aria-label={t('media_picker.placeholder.folder')} />
            <button type="submit" className="media-picker-secondary" disabled={creatingFolder || !newFolderName.trim()}>
              {creatingFolder ? t('media_picker.action.creating') : t('media_picker.action.create_folder')}
            </button>
          </form>
        </div>

        <div className="media-picker-body">
          {loading && <div className="media-picker-state">{t('media_picker.loading')}</div>}
          {!loading && error && <div className="media-picker-state media-picker-error">{error}</div>}
          {!loading && !error && !visibleMedia.length && <div className="media-picker-state">{t('media_picker.empty')}</div>}
          {!loading && !error && visibleMedia.length > 0 && (
            <div className="media-picker-grid">
              {visibleMedia.map(item => {
                const folder = isMediaFolder(item);
                const isSelected = selectedIds.has(item.id);
                return (
                  <button
                    type="button"
                    key={item.id || getMediaName(item)}
                    className={`media-picker-item${isSelected ? ' selected' : ''}`}
                    onClick={() => folder ? openFolder(item) : toggleSelection(item)}
                    aria-pressed={!folder && isSelected}>
                    <span className="media-picker-preview">
                      {folder ? '▰' : <MediaPreview item={item} />}
                    </span>
                    <span className="media-picker-name">{getMediaName(item)}</span>
                    <span className="media-picker-meta">{folder ? t('media_picker.type.folder') : `${t(`media_picker.type.${getMediaTypeLabel(item).toLowerCase()}`, getMediaTypeLabel(item))}${getMediaSize(item) ? ` · ${getMediaSize(item)}` : ''}`}</span>
                    {!folder && isSelected && <span className="media-picker-check">✓</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="media-picker-footer">
          <span>{selected.length ? t('media_picker.selection.count', {count: selected.length}) : t('media_picker.selection.prompt')}</span>
          <div>
            <button type="button" className="media-picker-secondary" onClick={closePicker}>{t('media_picker.action.cancel')}</button>
            <button type="button" className="media-picker-primary" disabled={!selected.length} onClick={confirmSelection}>{t('media_picker.action.use_selected')}</button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default MediaPickerModal;
